import { toast } from 'react-toastify'
import { postCPDataWithParams } from 'src/clinic/services'
import { DISPATCH_EVENT_KEYS } from 'src/consumer/constants'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getEventTypesFromRoomData } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsHelper'
import { broadcastCheckInUpdate, updateCheckInWithValidation, broadcastCheckInRemoval } from './checkInSyncService'
import { getClientDeviceInfo } from 'src/consumer/services/deviceInfoCollector'

function getSelectedCheckInEventOption(roomData) {
  return roomData?.selectedCheckInEventOption || roomData?.selectedCheckinEventOption
}

function getResolvedChamberRoomId(roomData) {
  return roomData?.chamberRoomId || roomData?.id
}

function buildEmrUsagePayload(roomData, extraParams = {}) {
  return {
    selectedCheckInEventOption: getSelectedCheckInEventOption(roomData),
    checkinMethod: roomData?.checkinMethod || roomData?.checkInMethod,
    clinicBrandId: roomData?.clinicBrandId,
    clinicLocationId: roomData?.clinicLocationId,
    chamberRoomId: getResolvedChamberRoomId(roomData),
    apiParam: 'emr-usage-log',
    clientDeviceInfo: getClientDeviceInfo(),
    ...extraParams,
  }
}

function buildCheckoutPayload(emrUsageLog, checkinRequestData = null) {
  const payload = {
    selectedCheckInEventOption:
      emrUsageLog?.selectedCheckInEventOption || emrUsageLog?.selectedCheckinEventOption,
    checkinMethod: emrUsageLog?.checkinMethod || emrUsageLog?.checkInMethod,
    clinicLocationId: emrUsageLog?.clinicLocationId,
    clinicBrandId: emrUsageLog?.clinicBrandId,
    chamberRoomId: emrUsageLog?.chamberRoomId,
    apiParam: 'emr-usage-log',
    clientDeviceInfo: getClientDeviceInfo(),
  }

  if (checkinRequestData) {
    payload.checkinRequestMentorId = checkinRequestData.mentorId
    payload.checkinRequestReason = checkinRequestData.reason
    payload.checkinRequestId = checkinRequestData.requestId
  }

  return payload
}

async function logAutoConnectState(roomData, logEventId, autoConnectState = {}) {
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/events`,
    buildEmrUsagePayload(roomData, {
      logEventId,
      autoConnectPaused: autoConnectState?.autoConnectPaused,
      autoConnectPauseDurationMs: autoConnectState?.autoConnectPauseDurationMs,
      autoConnectResumeAtEpochMs: autoConnectState?.autoConnectResumeAtEpochMs,
    }),
    {},
  )
  return response
}

export async function checkIntoRoom(roomData) {
  console.log(roomData)
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/checkin`,
    buildEmrUsagePayload(roomData),
    {},
  )

  if (response?.status === 200) {
    if (isCommonResponseSuccessfulV2(response)) {
      window.dispatchEvent(
        new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedIn, {
          detail: {
            roomData: response?.data?.data?.['chamber-room'],
            usageLog: response?.data?.data?.['emr-usage-log'],
          },
        }),
      )
    } else {
      console.error('error', response?.data?.message)
      toast.error(response?.data?.message || 'Could not check in')
    }
  } else {
    console.error('error', response.message)
    toast.error('Could not check in')
  }
  return response
}

export async function checkIntoRoomWithLocalStorageApplied(roomData) {
  const resp = await checkIntoRoom(roomData)
  if (resp?.status === 200) {
    const emrUsageLog = resp?.data?.data?.['emr-usage-log']
    const chamberRoom = resp?.data?.data?.['chamber-room']
    
    if (emrUsageLog) {
      // Update with validation timestamp and broadcast to other tabs
      updateCheckInWithValidation(emrUsageLog, chamberRoom)
      broadcastCheckInUpdate(emrUsageLog, chamberRoom)
    }
    
    if (chamberRoom) {
      localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
    }
    
    // Extract and store eventTypes from roomData
    if (chamberRoom?.checkInEventOptions) {
      const eventTypes = getEventTypesFromRoomData(
        chamberRoom.checkInEventOptions,
        [], // mentorEventTypes not available here, will be matched later
        chamberRoom?.clinicLocationId
      )
      if (eventTypes.length > 0) {
        localStorage.setItem('roomDataEventTypes', JSON.stringify(eventTypes))
        console.log('[checkIntoRoomWithLocalStorageApplied] Stored roomDataEventTypes:', eventTypes.length, 'event types')
      }
    }
  }
}

export async function peekIntoRoom(roomData) {
  console.log(roomData)
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/events`,
    buildEmrUsagePayload(roomData, {
      logEventId: 'enter-without-checkin',
    }),
    {},
  )
  return response
}

export async function peekOutFromRoom(roomData) {
  console.log(roomData)
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/events`,
    buildEmrUsagePayload(roomData, {
      logEventId: 'peek-out',
    }),
    {},
  )
  return response
}
export async function logJoinAuditRoom(roomData) {
  console.log(roomData)
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/events`,
    buildEmrUsagePayload(roomData, {
      logEventId: 'audit-checkin',
    }),
    {},
  )
  return response
}
export async function logLeaveAuditRoom(roomData) {
  console.log(roomData)
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/events`,
    buildEmrUsagePayload(roomData, {
      logEventId: 'audit-checkout',
    }),
    {},
  )
  return response
}
export async function logMentorIdle(roomData) {
  console.log(roomData)
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/events`,
    buildEmrUsagePayload(roomData, {
      logEventId: 'log-screen-idle',
    }),
    {},
  )
  return response
}

export async function logAutoConnectPaused(roomData, autoConnectState = {}) {
  return logAutoConnectState(roomData, 'auto-connect-paused', {
    ...autoConnectState,
    autoConnectPaused: true,
  })
}

export async function logAutoConnectResumed(roomData, autoConnectState = {}) {
  return logAutoConnectState(roomData, 'auto-connect-resumed', {
    ...autoConnectState,
    autoConnectPaused: false,
  })
}

export async function refreshCheckIntoRoom(roomData) {
  console.log(roomData)
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/checkin-refresh`,
    buildEmrUsagePayload(roomData),
    {},
  )

  if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
    window.dispatchEvent(
      new CustomEvent(DISPATCH_EVENT_KEYS.userRefreshCheckedIn, {
        detail: {
          roomData: response?.data?.data?.['chamber-room'],
          usageLog: response?.data?.data?.['emr-usage-log'],
        },
      }),
    )
  }
  return response
}

export async function presentInRoom(roomData) {
  console.log(roomData)
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/events`,
    buildEmrUsagePayload(roomData, {
      logEventId: 'presence-detected',
    }),
    {},
  )
  return response
}

export async function refreshCheckIntoRoomWithLocalStorageApplied(roomData) {
  const resp = await refreshCheckIntoRoom(roomData)
  if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
    const emrUsageLog = resp?.data?.data?.['emr-usage-log']
    const chamberRoom = resp?.data?.data?.['chamber-room']

    if (emrUsageLog) {
      // Update with validation timestamp and broadcast to other tabs
      updateCheckInWithValidation(emrUsageLog, chamberRoom)
      broadcastCheckInUpdate(emrUsageLog, chamberRoom)
    }
    
    if (chamberRoom) {
      localStorage.setItem('peekedInRoomDetails', JSON.stringify(chamberRoom))
      
      // Extract and store eventTypes from roomData
      if (chamberRoom?.checkInEventOptions) {
        const eventTypes = getEventTypesFromRoomData(
          chamberRoom.checkInEventOptions,
          [], // mentorEventTypes not available here, will be matched later
          chamberRoom?.clinicLocationId
        )
        if (eventTypes.length > 0) {
          localStorage.setItem('roomDataEventTypes', JSON.stringify(eventTypes))
          console.log('[refreshCheckIntoRoomWithLocalStorageApplied] Stored roomDataEventTypes:', eventTypes.length, 'event types')
        }
      }
    }
  }
}

export async function checkOutRoom(emrUsageLog, checkinRequestData = null) {
  console.log(emrUsageLog)
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/checkout`,
    buildCheckoutPayload(emrUsageLog, checkinRequestData),
    {},
  )
  if (response?.status === 200) {
    // Remove from localStorage and broadcast to other tabs
    localStorage.removeItem('checkedInDetails')
    localStorage.removeItem('peekedInRoomDetails')
    localStorage.removeItem('roomDataEventTypes')
    broadcastCheckInRemoval()
    
    window.dispatchEvent(
      new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedOut, {
        detail: {},
      }),
    )
  }
  return response
}

/**
 * Log a checkin request as ignored
 * @param {Object} roomData - Room data containing clinic and chamber room information
 * @param {Object} checkinRequestData - Checkin request data
 * @param {string} checkinRequestData.mentorId - ID of the mentor who requested checkin
 * @param {string} checkinRequestData.reason - Reason for the checkin request
 * @param {string} checkinRequestData.requestId - Unique ID for this checkin request
 */
export async function logCheckinRequestIgnored(roomData, checkinRequestData) {
  console.log('Logging checkin request ignored', { roomData, checkinRequestData })
  const response = await postCPDataWithParams(
    `/api/secure/teacher/emr/usage/log/events`,
    buildEmrUsagePayload(roomData, {
      logEventId: 'checkin-request-ignored',
      checkinRequestMentorId: checkinRequestData?.mentorId,
      checkinRequestReason: checkinRequestData?.reason,
      checkinRequestId: checkinRequestData?.requestId,
    }),
    {},
  )
  return response
}
